import { FC, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material'
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, Work as WorkIcon } from '@mui/icons-material'
import {
  useGetClientQuery,
  useGetProjectsByClientQuery,
  useUpdateClientMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
} from '../../redux/hooks.ts'
import { Layout } from '../../components/layout.tsx'
import { MainContent } from '../../components/main-content.tsx'
import { MainContentHeader } from '../../components/main-content-header.tsx'
import { MainContentBody } from '../../components/main-content-body.tsx'
import { ClientFormModal, ClientFormData } from '../../components/client-form-modal.tsx'
import { ProjectFormModal, ConfirmDialog } from '../../components/index.ts'
import { ProjectsTable } from './projects-table.tsx'
import type { ProjectModel, ProjectUpdate } from '@itemize/server/models'

export const ClientPage: FC = () => {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isProjectEditModalOpen, setIsProjectEditModalOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedProject, setSelectedProject] = useState<ProjectModel | null>(null)

  const { data: client, isLoading, error } = useGetClientQuery(clientId!)
  const {
    data: projects = [],
    isLoading: projectsLoading,
    error: projectsError,
  } = useGetProjectsByClientQuery(clientId!)
  const [updateClient, { isLoading: isUpdating }] = useUpdateClientMutation()
  const [updateProject, { isLoading: isUpdatingProject }] = useUpdateProjectMutation()
  const [deleteProject, { isLoading: isDeletingProject }] = useDeleteProjectMutation()

  if (isLoading) return <Typography>Loading...</Typography>
  if (error) return <Typography>Error loading client</Typography>
  if (!client) return <Typography>Client not found</Typography>

  const handleBack = () => {
    navigate('/clients')
  }

  const handleEdit = () => {
    setIsEditModalOpen(true)
  }

  const handleEditSubmit = async (data: ClientFormData) => {
    await updateClient({
      id: client.id,
      data: {
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
      },
    }).unwrap()
  }

  const handleEditClose = () => {
    setIsEditModalOpen(false)
  }

  const handleProjectView = (project: ProjectModel) => {
    navigate(`/clients/${clientId}/projects/${project.id}`)
  }

  const handleProjectEdit = (project: ProjectModel) => {
    setSelectedProject(project)
    setIsProjectEditModalOpen(true)
  }

  const handleProjectUpdate = async (data: ProjectUpdate) => {
    if (!selectedProject) return
    await updateProject({ id: selectedProject.id, data }).unwrap()
    setIsProjectEditModalOpen(false)
    setSelectedProject(null)
  }

  const handleProjectDelete = (project: ProjectModel) => {
    setSelectedProject(project)
    setIsDeleteDialogOpen(true)
  }

  const handleConfirmProjectDelete = async () => {
    if (!selectedProject) return
    await deleteProject(selectedProject.id).unwrap()
    setIsDeleteDialogOpen(false)
    setSelectedProject(null)
  }

  return (
    <Layout>
      <MainContent>
        <MainContentHeader
          breadcrumbs={[
            { label: 'Clients', href: '/clients' },
            { label: client.name, href: `/clients/${clientId}` },
          ]}
          buttons={
            <>
              <Button startIcon={<ArrowBackIcon />} onClick={handleBack} variant="outlined">
                Back to Clients
              </Button>
              <Button startIcon={<EditIcon />} onClick={handleEdit} variant="contained">
                Edit
              </Button>
            </>
          }
        />
        <MainContentBody>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }} component="div">
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Contact Information
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Name
                      </Typography>
                      <Typography variant="body1">{client.name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Email
                      </Typography>
                      <Typography variant="body1">{client.email || 'Not provided'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Phone
                      </Typography>
                      <Typography variant="body1">{client.phone || 'Not provided'}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} component="div">
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Details
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Client ID
                      </Typography>
                      <Chip label={client.id} size="small" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Created
                      </Typography>
                      <Typography variant="body1">
                        {new Date(client.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Last Updated
                      </Typography>
                      <Typography variant="body1">
                        {new Date(client.updatedAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12 }} component="div">
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <WorkIcon color="primary" />
                    <Typography variant="h6">Projects ({projects.length})</Typography>
                  </Box>

                  {projectsLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  ) : projectsError ? (
                    <Alert severity="error">Error loading projects</Alert>
                  ) : projects.length === 0 ? (
                    <Box textAlign="center" py={4}>
                      <WorkIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="body1" color="textSecondary">
                        No projects found for this client
                      </Typography>
                    </Box>
                  ) : (
                    <ProjectsTable
                      projects={projects}
                      onView={handleProjectView}
                      onEdit={handleProjectEdit}
                      onDelete={handleProjectDelete}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </MainContentBody>
      </MainContent>

      <ClientFormModal
        open={isEditModalOpen}
        onClose={handleEditClose}
        onSubmit={handleEditSubmit}
        initialData={{
          name: client.name,
          email: client.email || '',
          phone: client.phone || '',
        }}
        title="Edit Client"
        submitText="Update Client"
        isLoading={isUpdating}
      />

      <ProjectFormModal
        open={isProjectEditModalOpen}
        onClose={() => {
          setIsProjectEditModalOpen(false)
          setSelectedProject(null)
        }}
        onSubmit={handleProjectUpdate}
        initialData={
          selectedProject
            ? {
                name: selectedProject.name,
                clientId: selectedProject.clientId,
                description: selectedProject.description || undefined,
              }
            : undefined
        }
        title="Edit Project"
        submitText="Update Project"
        isLoading={isUpdatingProject}
      />

      <ConfirmDialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
          setSelectedProject(null)
        }}
        onConfirm={handleConfirmProjectDelete}
        title="Delete Project"
        message={`Are you sure you want to delete "${selectedProject?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isDeletingProject}
      />
    </Layout>
  )
}
